import React from 'react'
import { Facebook, Instagram, Linkedin, Mail, MapPin, MessageCircle, Phone, Twitter } from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-gray-100 text-gray-600 dark:bg-gray-950 dark:text-gray-300 transition-colors duration-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-0 mb-4">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/laundry-mate-logo.png" alt="LaundryMate" className="w-full h-full object-contain" />
              </div>
              <span className="text-gray-900 dark:text-white font-bold text-xl transition-colors duration-300">LaundryMate</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">
              Complete laundry shop management system. Streamline operations, engage customers, and grow your business.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/app.laundrymate"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-cyan-600 hover:text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-cyan-600 hover:text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-cyan-600 hover:text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-cyan-600 hover:text-white flex items-center justify-center transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-4 transition-colors duration-300">Product</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#features" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Features
                </a>
              </li>
              <li>
                <a href="#pricing" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#faq" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  FAQ
                </a>
              </li>
              <li>
                <a href="https://app.laundrymate.shop" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Login
                </a>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-4 transition-colors duration-300">Company</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  About Us
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Blog
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Careers
                </a>
              </li>
              <li>
                <a
                  href="https://m.me/946243658562372"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200"
                >
                  Contact
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  Partners
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-4 transition-colors duration-300">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a
                  href="https://m.me/946243658562372"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200"
                >
                  Message us on Messenger
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a href="mailto:orders@laundrymate.shop" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  orders@laundrymate.shop
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a href="tel:+639331844903" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                  +63 933 184 4903
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <span>Manila, Philippines</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 dark:border-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <p>© {currentYear} LaundryMate. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                Terms of Service
              </a>
              <a href="#" className="hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors duration-200">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
